import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { colorFor } from "./palette";

const TARGET_LABEL = "Target";
const TARGET_KEY = "__target__";
const TOP_KEY = "__top__";
const TARGET_COLOR = "#cfa77b";

/**
 * A report's groups, each bar stacked by a second property (the "By product"
 * view). Used for the pipeline against target and for closed lost reasons.
 *
 * The total sits above each stack so the bar reads the same as it does
 * unstacked; the target, when there is one, stays a bar of its own and a line.
 */
export default function StackedBreakdownChart({
  groups = [],
  breakdown,
  target,
  targetAsBar = false,
  formatValue,
  onGroupClick,
}) {
  const format = formatValue || ((v) => new Intl.NumberFormat("en-GB").format(v));
  const compact = (value) =>
    new Intl.NumberFormat("en-GB", { notation: "compact", maximumFractionDigits: 1 }).format(value);
  const labels = breakdown?.labels || [];

  if (!groups.length) {
    return <p className="py-12 text-center text-sm text-[#4a5b68]">No records match this report.</p>;
  }

  const hasTarget = typeof target === "number";
  const rows = groups.map((group) => ({
    name: group.label,
    total: group.value,
    [TOP_KEY]: 0,
    ...(group.parts || {}),
  }));
  const data =
    hasTarget && targetAsBar
      ? [{ name: TARGET_LABEL, total: target, [TARGET_KEY]: target, [TOP_KEY]: 0 }, ...rows]
      : rows;

  return (
    <ResponsiveContainer width="100%" height={380} minWidth={280}>
      <BarChart data={data} margin={{ top: 26, right: 16, left: 4, bottom: 0 }}>
        <CartesianGrid stroke="#dddddd" vertical={false} />
        <XAxis dataKey="name" tickLine={false} axisLine={false} interval={0} tick={{ fill: "#4a5b68", fontSize: 11 }} />
        <YAxis tickLine={false} axisLine={false} tick={{ fill: "#4a5b68", fontSize: 11 }} tickFormatter={compact} />
        <Tooltip
          cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
          contentStyle={{ borderRadius: 4, borderColor: "#cccccc", boxShadow: "0 4px 12px rgba(51, 71, 91, 0.12)" }}
          formatter={(value, name) => (name === TOP_KEY ? [null, null] : [format(value), name === TARGET_KEY ? TARGET_LABEL : name])}
        />
        <Legend
          wrapperStyle={{ fontSize: 11, color: "#4a5b68" }}
          payload={labels.map((label, index) => ({ value: label, type: "square", color: colorFor(label, index, {}) }))}
        />
        {hasTarget && targetAsBar && (
          <Bar dataKey={TARGET_KEY} stackId="s" fill={TARGET_COLOR} isAnimationActive={false} maxBarSize={64} />
        )}
        {labels.map((label, index) => (
          <Bar
            key={label}
            dataKey={label}
            stackId="s"
            fill={colorFor(label, index, {})}
            isAnimationActive={false}
            maxBarSize={64}
            cursor={onGroupClick ? "pointer" : undefined}
            onClick={(entry) => {
              const name = entry?.name ?? entry?.payload?.name;
              if (onGroupClick && name && name !== TARGET_LABEL) onGroupClick(name);
            }}
          />
        ))}
        {/* A zero-height bar on top of each stack, carrying the stack's total. */}
        <Bar dataKey={TOP_KEY} stackId="s" fill="transparent" isAnimationActive={false} legendType="none">
          <LabelList dataKey="total" position="top" fill="#323c44" fontSize={11} formatter={compact} />
        </Bar>
        {hasTarget && (
          <ReferenceLine y={target} stroke={TARGET_COLOR} strokeDasharray="5 4" strokeWidth={1.25} ifOverflow="extendDomain" />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}
